import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { MembersPanel } from '../components/MembersPanel.js';
import { Panel } from '../components/Panel.js';
import { useConfirm } from '../components/ui/ConfirmDialog.js';
import {
  Badge,
  Button,
  Card,
  Field,
  Icon,
  ListPagination,
  Modal,
  PageHeader,
  PagedTableShell,
  Select,
  Stat,
  Table,
  Tabs,
  Td,
  TextInput,
  useClientPaging,
  type IconName,
} from '../components/ui/index.js';
import {
  TenantBillingFields,
  emptyTenantBilling,
  parseTenantBilling,
  tenantBillingFromRow,
  type TenantBillingValues,
} from '../components/TenantBillingFields.js';
import { ApiError, apiRequest } from '../lib/api.js';
import { useAdminLocale } from '../lib/admin-locale.js';
import { readJson, useRemote } from '../lib/remote.js';
import { useSession } from '../lib/session.js';
import { timezoneChoices } from '../lib/timezones.js';
import { formatMoney, InvoicePaymentDialog, type InvoiceRow } from './BillingPage.js';

interface TenantRow {
  id: string;
  name: string;
  status: 'ACTIVE' | 'SUSPENDED';
  timezone: string;
  creditDays?: number;
  taxId?: string | null;
  taxOffice?: string | null;
  profession?: string | null;
  street?: string | null;
  streetNumber?: string | null;
  city?: string | null;
  postalCode?: string | null;
  phone?: string | null;
  email?: string | null;
  notes?: string | null;
  createdAt: string;
  sites?: number;
  users?: number;
  monthlyAnswers?: { completed: number; quota: number };
  owedCents?: number;
}

interface SiteRow {
  id: string;
  name: string;
  canonicalOrigin: string;
  status: string;
}

interface PlanLimits {
  sites: number;
  monthlyAnswers: number;
  siteMonthlyAnswers?: number | null;
  maxConcurrent?: number;
  spendingCeilingUsd?: number | null;
  ingestionMonthlyTokens?: number;
  maxSources?: number;
  maxPdfBytes?: number;
  maxExtractedChars?: number;
  maxInputTokens?: number;
  maxOutputTokens?: number;
}

interface PlanRow {
  id: string;
  code: string;
  name: string;
  limits: PlanLimits;
  monthlyPriceCents: number;
  currency: string;
  createdAt: string;
}

interface SubscriptionRow {
  id: string;
  status: 'ACTIVE' | 'ENDED' | 'SUSPENDED';
  startsAt: string;
  endsAt: string | null;
  plan: {
    code: string;
    name: string;
    limits: PlanLimits;
    monthlyPriceCents?: number;
    currency?: string;
  };
}

const linkClass = 'font-medium text-blue-600 hover:text-blue-800 dark:hover:text-blue-200';

function errorText(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function formatDate(value: string | null | undefined, locale: 'el' | 'en'): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(locale === 'en' ? 'en-GB' : 'el-GR');
}

const TENANT_TABS = [
  { id: 'dashboard', el: 'Dashboard', en: 'Dashboard', icon: 'chart' },
  { id: 'profile', el: 'Στοιχεία', en: 'Details', icon: 'building' },
  { id: 'subscription', el: 'Συνδρομή', en: 'Subscription', icon: 'layers' },
  { id: 'sites', el: 'Ιστοσελίδες', en: 'Websites', icon: 'globe' },
  { id: 'members', el: 'Μέλη', en: 'Members', icon: 'users' },
  { id: 'danger', el: 'Διαγραφή', en: 'Delete', icon: 'trash', tone: 'danger' },
] as const satisfies ReadonlyArray<{
  id: string;
  el: string;
  en: string;
  icon: IconName;
  tone?: 'danger';
}>;

type TenantTabId = (typeof TENANT_TABS)[number]['id'];

interface TenantUsage {
  answers: { quota: number; completed: number };
  conversations: number;
  messages: number;
}

function accountTotals(invoices: InvoiceRow[]) {
  const live = invoices.filter((invoice) => invoice.status !== 'VOID');
  const payments = live
    .flatMap((invoice) =>
      invoice.payments.map((payment) => ({
        ...payment,
        number: invoice.number,
        currency: invoice.currency,
      })),
    )
    .sort((a, b) => b.paidOn.localeCompare(a.paidOn));
  const now = Date.now();
  return {
    paidCents: live.reduce((sum, invoice) => sum + invoice.paidCents, 0),
    owedCents: live.reduce((sum, invoice) => sum + invoice.balanceCents, 0),
    liveCount: live.length,
    openCount: live.filter((invoice) => invoice.balanceCents > 0).length,
    overdueCount: live.filter(
      (invoice) => invoice.balanceCents > 0 && new Date(invoice.dueAt).getTime() < now,
    ).length,
    paymentCount: payments.length,
    payments,
  };
}

function isTenantTab(value: string | null): value is TenantTabId {
  return TENANT_TABS.some((tab) => tab.id === value);
}

export function TenantsPage() {
  const { locale, text } = useAdminLocale();
  const tenants = useRemote<TenantRow[]>('/v1/tenants', null);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const filtered = (tenants.data ?? []).filter((tenant) =>
    tenant.name.toLocaleLowerCase('el').includes(query.trim().toLocaleLowerCase('el')),
  );
  const paging = useClientPaging(filtered, { resetKey: query });
  const rows = paging.rows;
  const owedTotal = filtered.reduce((sum, tenant) => sum + (tenant.owedCents ?? 0), 0);
  const clientCount = filtered.length;
  const clientCountLabel =
    clientCount === 1
      ? text('1 πελάτης', '1 client')
      : text(
          `${clientCount.toLocaleString('el-GR')} πελάτες`,
          `${clientCount.toLocaleString('en-GB')} clients`,
        );

  return (
    <section className="admin-page">
      <PageHeader
        description={text(
          'Κάθε πελάτης βλέπει μόνο τα websites και τα δεδομένα που του έχουν ανατεθεί.',
          'Each client can only see the websites and data assigned to them.',
        )}
        actions={
          <Button type="button" onClick={() => setCreating(true)}>
            <Icon name="plus" className="size-4" /> {text('Νέος πελάτης', 'New client')}
          </Button>
        }
      />
      {creating ? (
        <NewTenantDialog
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void tenants.mutate();
          }}
        />
      ) : null}
      <div className="min-w-0 space-y-4">
        <TextInput
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={text('Αναζήτηση πελάτη', 'Search clients')}
          aria-label={text('Αναζήτηση πελάτη', 'Search clients')}
        />
        <Panel
          loading={!tenants.data && !tenants.error}
          error={tenants.error?.message ?? null}
          empty={filtered.length === 0}
          emptyLabel={text('Δεν υπάρχουν πελάτες.', 'No clients found.')}
        >
          <Card flush>
            <PagedTableShell pagination={paging.total > 0 ? paging.pager : null}>
              <Table
                head={[
                  text('Πελάτης', 'Client'),
                  'Websites',
                  text('Χρήστες', 'Users'),
                  text('Απαντήσεις μήνα', 'Monthly answers'),
                  text('Χρωστάει', 'Amount due'),
                  text('Κατάσταση', 'Status'),
                ]}
                foot={
                  <tr>
                    <Td className="whitespace-nowrap">
                      {text('Σύνολο', 'Total')} · {clientCountLabel}
                    </Td>
                    <Td />
                    <Td />
                    <Td />
                    <Td
                      className={
                        owedTotal > 0 ? 'font-semibold text-rose-700 dark:text-rose-300' : undefined
                      }
                    >
                      {formatMoney(owedTotal)}
                    </Td>
                    <Td />
                  </tr>
                }
              >
                {rows.map((tenant) => (
                  <tr key={tenant.id}>
                    <Td>
                      <Link className={linkClass} to={`/platform/tenants/${tenant.id}`}>
                        {tenant.name}
                      </Link>
                    </Td>
                    <Td muted>{tenant.sites ?? '—'}</Td>
                    <Td muted>{tenant.users ?? '—'}</Td>
                    <Td muted>
                      {tenant.monthlyAnswers
                        ? `${tenant.monthlyAnswers.completed.toLocaleString(locale === 'en' ? 'en-GB' : 'el-GR')} / ${tenant.monthlyAnswers.quota.toLocaleString(locale === 'en' ? 'en-GB' : 'el-GR')}`
                        : '—'}
                    </Td>
                    <Td
                      className={
                        (tenant.owedCents ?? 0) > 0
                          ? 'font-medium text-rose-700 dark:text-rose-300'
                          : undefined
                      }
                    >
                      {formatMoney(tenant.owedCents ?? 0)}
                    </Td>
                    <Td>
                      <Badge
                        value={tenant.status}
                        label={
                          tenant.status === 'ACTIVE'
                            ? text('Ενεργός', 'Active')
                            : text('Ανενεργός', 'Inactive')
                        }
                      />
                    </Td>
                  </tr>
                ))}
              </Table>
            </PagedTableShell>
          </Card>
        </Panel>
      </div>
    </section>
  );
}

function NewTenantDialog(props: { onClose: () => void; onCreated: () => void }) {
  const { text } = useAdminLocale();
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('Europe/Athens');
  const [billing, setBilling] = useState<TenantBillingValues>(emptyTenantBilling);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function create(event: FormEvent): Promise<void> {
    event.preventDefault();
    const profile = parseTenantBilling(billing);
    if (!profile.ok) {
      setError(profile.message);
      return;
    }
    setPending(true);
    setError(null);
    try {
      await readJson(
        '/v1/tenants',
        { method: 'POST', body: JSON.stringify({ name, timezone, ...profile.value }) },
        null,
      );
      props.onCreated();
    } catch (caught) {
      setError(errorText(caught, text('Η δημιουργία απέτυχε', 'Creation failed')));
    } finally {
      setPending(false);
    }
  }

  return (
    <Modal
      wide
      title={text('Νέος πελάτης', 'New client')}
      description={text(
        'Μετά τη δημιουργία: ανάθεση πακέτου, ιστοσελίδα και πρόσκληση του πρώτου διαχειριστή.',
        'After creation: assign a plan and website, then invite the first administrator.',
      )}
      onClose={props.onClose}
    >
      <form className="grid gap-4" onSubmit={(event) => void create(event)}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={text('Επωνυμία', 'Company name')}>
            <TextInput
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={text('π.χ. Example Hotels', 'e.g. Example Hotels')}
            />
          </Field>
          <Field label={text('Ζώνη ώρας', 'Time zone')}>
            <Select
              className="mt-1.5"
              required
              value={timezone}
              onChange={setTimezone}
              searchable
              options={timezoneChoices(timezone).map((zone) => ({
                value: zone,
                label: zone,
              }))}
            />
          </Field>
        </div>
        <TenantBillingFields values={billing} onChange={setBilling} />
        {error ? <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={props.onClose}>
            <Icon name="x" className="size-4" /> {text('Άκυρο', 'Cancel')}
          </Button>
          <Button type="submit" disabled={pending}>
            <Icon name="plus" className="size-4" />
            {pending ? text('Δημιουργία…', 'Creating…') : text('Δημιουργία', 'Create')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function TenantDetailPage(props: { onOpenAsTenant: (tenantId: string) => void }) {
  const { locale, text } = useAdminLocale();
  const { tenantId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const tab: TenantTabId = isTenantTab(requested) ? requested : 'dashboard';
  const { user } = useSession();
  const confirmDialog = useConfirm();
  const tenants = useRemote<TenantRow[]>('/v1/tenants', null);
  const tenant = tenants.data?.find((row) => row.id === tenantId);
  const sites = useRemote<SiteRow[]>(`/v1/tenants/${tenantId}/sites`, null);
  const plans = useRemote<PlanRow[]>('/v1/plans', null);
  const subscription = useRemote<SubscriptionRow>('/v1/subscription', tenantId);
  const invoices = useRemote<InvoiceRow[]>(`/v1/tenants/${tenantId}/invoices`, null);
  const usage = useRemote<TenantUsage>('/v1/usage', tenantId);
  const noSubscription =
    subscription.error instanceof ApiError && subscription.error.status === 404;
  const account = accountTotals(invoices.data ?? []);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  function notify(tone: 'ok' | 'error', messageText: string): void {
    setMessage({ tone, text: messageText });
  }

  function openTab(next: TenantTabId): void {
    const copy = new URLSearchParams(params);
    if (next === 'dashboard') copy.delete('tab');
    else copy.set('tab', next);
    setParams(copy, { replace: true });
  }

  async function updateTenant(data: Record<string, unknown>): Promise<void> {
    try {
      await readJson(
        `/v1/tenants/${tenantId}`,
        { method: 'PATCH', body: JSON.stringify(data) },
        null,
      );
      await tenants.mutate();
      notify('ok', text('Αποθηκεύτηκε.', 'Saved.'));
    } catch (error) {
      notify('error', errorText(error, text('Η αποθήκευση απέτυχε', 'Save failed')));
    }
  }

  return (
    <section className="admin-page">
      <PageHeader
        title={tenant?.name ?? text('Πελάτης', 'Client')}
        description={
          tenant
            ? text(
                `Ζώνη ώρας ${tenant.timezone} · από ${formatDate(tenant.createdAt, locale)}`,
                `Time zone ${tenant.timezone} · since ${formatDate(tenant.createdAt, locale)}`,
              )
            : undefined
        }
        actions={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => props.onOpenAsTenant(tenantId)}
            >
              <Icon name="arrow" className="size-4" />{' '}
              {text('Άνοιγμα ως πελάτης', 'Open as client')}
            </Button>
            {tenant ? (
              <Button
                type="button"
                variant={tenant.status === 'ACTIVE' ? 'danger' : 'primary'}
                onClick={() => {
                  void (async () => {
                    const next = tenant.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
                    if (next === 'SUSPENDED') {
                      const ok = await confirmDialog({
                        title: text('Αναστολή πελάτη', 'Suspend client'),
                        message: text(
                          'Αναστολή πελάτη; Το widget και το pairing σταματούν.',
                          'Suspend this client? The widget and pairing will stop.',
                        ),
                        confirmLabel: text('Αναστολή', 'Suspend'),
                        danger: true,
                      });
                      if (!ok) return;
                    }
                    await updateTenant({ status: next });
                  })();
                }}
              >
                <Icon name={tenant.status === 'ACTIVE' ? 'ban' : 'check'} className="size-4" />
                {tenant.status === 'ACTIVE'
                  ? text('Αναστολή', 'Suspend')
                  : text('Επανενεργοποίηση', 'Reactivate')}
              </Button>
            ) : null}
          </>
        }
      />
      <p className="text-sm">
        <Link className={`${linkClass} inline-flex items-center gap-1`} to="/platform/tenants">
          <Icon name="arrow" className="size-4 rotate-180" />{' '}
          {text('Όλοι οι πελάτες', 'All clients')}
        </Link>
      </p>
      {message ? (
        <p
          className={`text-sm ${message.tone === 'ok' ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}
        >
          {message.text}
        </p>
      ) : null}
      {tenants.error ? (
        <Panel loading={false} error={tenants.error.message} empty={false} emptyLabel="">
          {null}
        </Panel>
      ) : null}

      <Tabs
        items={TENANT_TABS.map((item) => ({
          id: item.id,
          label: text(item.el, item.en),
          icon: item.icon,
          ...('tone' in item ? { tone: item.tone } : {}),
        }))}
        value={tab}
        onChange={(id) => {
          if (isTenantTab(id)) openTab(id);
        }}
      />

      {tab === 'dashboard' ? (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-3">
            <Stat
              icon="shield"
              label={text('Κατάσταση', 'Status')}
              value={
                tenant ? (
                  <Badge
                    value={tenant.status}
                    label={
                      tenant.status === 'ACTIVE'
                        ? text('Ενεργός', 'Active')
                        : text('Σε αναστολή', 'Suspended')
                    }
                  />
                ) : (
                  '—'
                )
              }
            />
            <Stat
              icon="globe"
              label={text('Ιστοσελίδες', 'Websites')}
              value={sites.data?.length ?? '—'}
            />
            <Stat
              icon="layers"
              label={text('Πακέτο', 'Plan')}
              value={
                subscription.data?.plan.name ??
                (noSubscription ? text('Χωρίς πακέτο', 'No plan') : '—')
              }
              hint={
                subscription.data
                  ? subscription.data.status
                  : text('Απαιτείται για δημιουργία ιστοσελίδας', 'Required to create a website')
              }
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Stat
              icon="chat"
              label={text('Απαντήσεις μήνα', 'Monthly answers')}
              value={
                usage.data
                  ? `${usage.data.answers.completed.toLocaleString(locale === 'en' ? 'en-GB' : 'el-GR')} / ${usage.data.answers.quota.toLocaleString(locale === 'en' ? 'en-GB' : 'el-GR')}`
                  : '—'
              }
              meter={
                usage.data && usage.data.answers.quota > 0
                  ? (usage.data.answers.completed / usage.data.answers.quota) * 100
                  : undefined
              }
              hint={
                usage.data
                  ? text(
                      `${usage.data.conversations.toLocaleString('el-GR')} συνομιλίες · ${usage.data.messages.toLocaleString('el-GR')} μηνύματα`,
                      `${usage.data.conversations.toLocaleString('en-GB')} conversations · ${usage.data.messages.toLocaleString('en-GB')} messages`,
                    )
                  : text('Τρέχων μήνας του πελάτη', "Client's current month")
              }
            />
            <Stat
              icon="check"
              label={text('Έχει πληρώσει', 'Paid')}
              value={invoices.data ? formatMoney(account.paidCents) : '—'}
              hint={
                account.paymentCount === 1
                  ? text('1 καταβολή', '1 payment')
                  : text(
                      `${account.paymentCount.toLocaleString('el-GR')} καταβολές`,
                      `${account.paymentCount.toLocaleString('en-GB')} payments`,
                    )
              }
            />
            <Stat
              icon="alert"
              label={text('Χρωστάει', 'Amount due')}
              value={invoices.data ? formatMoney(account.owedCents) : '—'}
              accent={account.owedCents > 0}
              hint={
                account.openCount === 0
                  ? text('Κανένα ανοιχτό τιμολόγιο', 'No open invoices')
                  : account.openCount === 1
                    ? text('1 ανοιχτό τιμολόγιο', '1 open invoice')
                    : text(
                        `${account.openCount.toLocaleString('el-GR')} ανοιχτά τιμολόγια`,
                        `${account.openCount.toLocaleString('en-GB')} open invoices`,
                      )
              }
            />
            <Stat
              icon="file"
              label={text('Τιμολόγια', 'Invoices')}
              value={
                invoices.data
                  ? account.liveCount.toLocaleString(locale === 'en' ? 'en-GB' : 'el-GR')
                  : '—'
              }
              hint={
                account.overdueCount > 0
                  ? text(`${account.overdueCount} εκπρόθεσμα`, `${account.overdueCount} overdue`)
                  : text('Χωρίς ακυρωμένα', 'Excludes void invoices')
              }
            />
          </div>

          <div className="grid gap-6 xl:grid-cols-2">
            <Card
              title={text('Τιμολόγια', 'Invoices')}
              description={text(
                'Πληρωμένο ποσό και υπόλοιπο ανά τιμολόγιο.',
                'Paid amount and balance for each invoice.',
              )}
            >
              {(invoices.data ?? []).filter((invoice) => invoice.status !== 'VOID').length === 0 ? (
                <p className="text-sm text-slate-500">
                  {text('Δεν έχει εκδοθεί τιμολόγιο.', 'No invoices have been issued.')}
                </p>
              ) : (
                <Table
                  head={[
                    text('Τιμολόγιο', 'Invoice'),
                    text('Ποσό', 'Amount'),
                    text('Πληρώθηκε', 'Paid'),
                    text('Υπόλοιπο', 'Balance'),
                    '',
                  ]}
                >
                  {(invoices.data ?? [])
                    .filter((invoice) => invoice.status !== 'VOID')
                    .slice(0, 6)
                    .map((invoice) => (
                      <tr key={invoice.id}>
                        <Td>
                          <span className="font-medium text-slate-900 dark:text-slate-100">
                            {invoice.number}
                          </span>
                          <span className="mt-0.5 block text-xs text-slate-500">
                            {text('Λήξη', 'Due')} {formatDate(invoice.dueAt, locale)}
                          </span>
                        </Td>
                        <Td>{formatMoney(invoice.amountCents, invoice.currency)}</Td>
                        <Td>{formatMoney(invoice.paidCents, invoice.currency)}</Td>
                        <Td>{formatMoney(invoice.balanceCents, invoice.currency)}</Td>
                        <Td>
                          <Badge
                            value={invoice.displayStatus}
                            label={
                              invoice.displayStatus === 'OPEN'
                                ? text('Απλήρωτο', 'Unpaid')
                                : invoice.displayStatus === 'PARTIAL'
                                  ? text('Μερική', 'Partial')
                                  : undefined
                            }
                          />
                        </Td>
                      </tr>
                    ))}
                </Table>
              )}
            </Card>
            <Card
              title={text('Καταβολές', 'Payments')}
              description={text(
                'Τι έχει καταχωριστεί ως πληρωμή.',
                'Payments recorded against invoices.',
              )}
            >
              {account.payments.length === 0 ? (
                <p className="text-sm text-slate-500">
                  {text('Δεν υπάρχει καταχωρισμένη πληρωμή.', 'No payments recorded.')}
                </p>
              ) : (
                <Table
                  head={[
                    text('Ημερομηνία', 'Date'),
                    text('Τιμολόγιο', 'Invoice'),
                    text('Ποσό', 'Amount'),
                    text('Σημείωση', 'Note'),
                  ]}
                >
                  {account.payments.slice(0, 6).map((payment) => (
                    <tr key={payment.id}>
                      <Td>{formatDate(payment.paidOn, locale)}</Td>
                      <Td>{payment.number}</Td>
                      <Td>{formatMoney(payment.amountCents, payment.currency)}</Td>
                      <Td muted>{payment.note || '—'}</Td>
                    </tr>
                  ))}
                </Table>
              )}
            </Card>
          </div>
        </div>
      ) : null}

      {tab === 'profile' ? (
        <div className="max-w-3xl">
          <TenantProfileCard key={tenantId} tenant={tenant} onSave={updateTenant} />
        </div>
      ) : null}

      {tab === 'subscription' ? (
        <SubscriptionCard
          tenantId={tenantId}
          plans={plans.data ?? []}
          subscription={subscription.data}
          loading={!subscription.data && !subscription.error}
          onChanged={() => {
            void subscription.mutate();
          }}
          onNotice={notify}
        />
      ) : null}

      {tab === 'sites' ? (
        <TenantSitesCard
          tenantId={tenantId}
          sites={sites.data ?? []}
          loading={!sites.data && !sites.error}
          error={sites.error?.message ?? null}
          hasPlan={Boolean(subscription.data)}
          onChanged={() => {
            void sites.mutate();
          }}
          onOpenAsTenant={props.onOpenAsTenant}
        />
      ) : null}

      {tab === 'members' ? (
        <Card
          title={text('Μέλη πελάτη', 'Client members')}
          description={text(
            'Προσκαλέστε τον πρώτο διαχειριστή του πελάτη. Οι ενέργειες καταγράφονται στο audit του πελάτη.',
            "Invite the client's first administrator. Actions are recorded in the client audit log.",
          )}
        >
          <MembersPanel canWrite tenantId={tenantId} currentUserId={user?.id} />
        </Card>
      ) : null}

      {tab === 'danger' && tenant ? (
        <TenantDeleteCard
          tenantId={tenantId}
          tenantName={tenant.name}
          suspended={tenant.status === 'SUSPENDED'}
        />
      ) : null}
    </section>
  );
}

function TenantDeleteCard(props: { tenantId: string; tenantName: string; suspended: boolean }) {
  const { text } = useAdminLocale();
  const navigate = useNavigate();
  const confirmDialog = useConfirm();
  const catalog = useRemote<Array<{ tenant: { id: string }; connection?: string }>>(
    '/v1/platform/sites',
    null,
  );
  const [confirmName, setConfirmName] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const connected = (catalog.data ?? []).filter(
    (site) => site.tenant.id === props.tenantId && site.connection === 'connected',
  ).length;
  const blockers: string[] = [];
  if (!props.suspended) {
    blockers.push(
      text(
        'Αναστείλετε πρώτα τον πελάτη από το κουμπί Αναστολή.',
        'Suspend the client first using the Suspend button.',
      ),
    );
  }
  if (catalog.error) {
    blockers.push(
      text('Δεν φορτώθηκαν οι συνδέσεις WordPress.', 'WordPress connections could not load.'),
    );
  } else if (!catalog.data) {
    blockers.push(text('Έλεγχος συνδέσεων WordPress…', 'Checking WordPress connections…'));
  } else if (connected > 0) {
    blockers.push(
      connected === 1
        ? text(
            'Υπάρχει 1 ενεργή σύνδεση WordPress. Ανακαλέστε την από την ιστοσελίδα.',
            'There is 1 active WordPress connection. Revoke it from the website.',
          )
        : text(
            `Υπάρχουν ${connected} ενεργές συνδέσεις WordPress. Ανακαλέστε τις από τις ιστοσελίδες.`,
            `There are ${connected} active WordPress connections. Revoke them from the websites.`,
          ),
    );
  }
  const canDelete = blockers.length === 0 && confirmName.trim() === props.tenantName;

  return (
    <Card
      title={text('Διαγραφή πελάτη', 'Delete client')}
      description={text(
        'Οριστική διαγραφή του πελάτη και όλων των δεδομένων του. Επιτρέπεται μόνο όταν είναι σε αναστολή και καμία ιστοσελίδα δεν έχει ενεργή σύνδεση WordPress.',
        'Permanently delete the client and all their data. This is only allowed while suspended and when no website has an active WordPress connection.',
      )}
    >
      {blockers.length > 0 ? (
        <ul className="mb-4 list-disc space-y-1 pl-5 text-sm text-amber-900 dark:text-amber-200">
          {blockers.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : (
        <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
          {text(
            'Θα διαγραφούν ιστοσελίδες, γνώση, συνομιλίες, επαφές, μέλη, συνδρομή και τιμολόγια. Οι λογαριασμοί χρηστών μένουν. Η ενέργεια δεν αναιρείται.',
            'Websites, knowledge, conversations, contacts, members, the subscription, and invoices will be deleted. User accounts remain. This action cannot be undone.',
          )}
        </p>
      )}
      <Field
        label={text(
          'Πληκτρολογήστε την επωνυμία του πελάτη για επιβεβαίωση',
          'Type the client company name to confirm',
        )}
      >
        <TextInput
          value={confirmName}
          onChange={(event) => setConfirmName(event.target.value)}
          placeholder={props.tenantName}
          disabled={pending || blockers.length > 0}
        />
      </Field>
      {error ? <p className="mt-3 text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}
      <div className="mt-4">
        <Button
          type="button"
          variant="danger"
          disabled={!canDelete || pending}
          onClick={() => {
            void (async () => {
              const confirmed = await confirmDialog({
                title: text('Οριστική διαγραφή', 'Permanently delete'),
                message: text(
                  `Να διαγραφεί οριστικά ο πελάτης «${props.tenantName}»;\n\nΘα σβηστούν όλα τα δεδομένα του και η ενέργεια δεν αναιρείται.`,
                  `Permanently delete client “${props.tenantName}”?\n\nAll their data will be deleted and this action cannot be undone.`,
                ),
                confirmLabel: text('Διαγραφή', 'Delete'),
                danger: true,
              });
              if (!confirmed) return;
              setPending(true);
              setError(null);
              try {
                const response = await apiRequest(`/v1/tenants/${props.tenantId}`, {
                  method: 'DELETE',
                  headers: { 'X-Tenant-Id': '' },
                });
                if (!response.ok) {
                  const body: unknown = await response.json().catch(() => null);
                  const message =
                    body && typeof body === 'object' && 'error' in body
                      ? String(
                          (body as { error?: { message?: string } }).error?.message ??
                            text('Η διαγραφή απέτυχε', 'Deletion failed'),
                        )
                      : text('Η διαγραφή απέτυχε', 'Deletion failed');
                  throw new Error(message);
                }
                navigate('/platform/tenants');
              } catch (caught: unknown) {
                setError(
                  caught instanceof Error
                    ? caught.message
                    : text('Η διαγραφή απέτυχε', 'Deletion failed'),
                );
              } finally {
                setPending(false);
              }
            })();
          }}
        >
          <Icon name="trash" className="size-4" />
          {pending
            ? text('Διαγραφή…', 'Deleting…')
            : text('Οριστική διαγραφή', 'Permanently delete')}
        </Button>
      </div>
    </Card>
  );
}

function TenantProfileCard(props: {
  tenant: TenantRow | undefined;
  onSave: (data: Record<string, unknown>) => Promise<void>;
}) {
  const { text } = useAdminLocale();
  const [name, setName] = useState<string | null>(null);
  const [timezone, setTimezone] = useState<string | null>(null);
  const [billing, setBilling] = useState<TenantBillingValues | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const currentName = name ?? props.tenant?.name ?? '';
  const currentZone = timezone ?? props.tenant?.timezone ?? '';
  const currentBilling =
    billing ?? (props.tenant ? tenantBillingFromRow(props.tenant) : emptyTenantBilling());
  return (
    <Card
      title={text('Στοιχεία', 'Details')}
      description={text(
        'Επωνυμία, ζώνη ώρας και στοιχεία τιμολόγησης.',
        'Company name, time zone, and billing details.',
      )}
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          const profile = parseTenantBilling(currentBilling);
          if (!profile.ok) {
            setError(profile.message);
            return;
          }
          setError(null);
          setPending(true);
          void props
            .onSave({ name: currentName, timezone: currentZone, ...profile.value })
            .finally(() => setPending(false));
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={text('Επωνυμία', 'Company name')}>
            <TextInput
              required
              value={currentName}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
          <Field label={text('Ζώνη ώρας', 'Time zone')}>
            <Select
              className="mt-1.5"
              required
              value={currentZone}
              disabled={!props.tenant}
              searchable
              onChange={setTimezone}
              options={[
                ...(currentZone === '' ? [{ value: '', label: '—' }] : []),
                ...timezoneChoices(currentZone || 'Europe/Athens').map((zone) => ({
                  value: zone,
                  label: zone,
                })),
              ]}
            />
          </Field>
        </div>
        <TenantBillingFields
          values={currentBilling}
          disabled={!props.tenant}
          onChange={setBilling}
        />
        {error ? <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}
        <Button type="submit" variant="secondary" disabled={pending || !props.tenant}>
          <Icon name="save" className="size-4" /> {text('Αποθήκευση', 'Save')}
        </Button>
      </form>
    </Card>
  );
}

function SubscriptionCard(props: {
  tenantId: string;
  plans: PlanRow[];
  subscription: SubscriptionRow | undefined;
  loading: boolean;
  onChanged: () => void;
  onNotice: (tone: 'ok' | 'error', text: string) => void;
}) {
  const { locale, text } = useAdminLocale();
  const [planCode, setPlanCode] = useState('');
  const [pending, setPending] = useState(false);
  const [paying, setPaying] = useState<'full' | 'partial' | null>(null);
  const invoices = useRemote<InvoiceRow[]>(`/v1/tenants/${props.tenantId}/invoices`, null);
  const selected = planCode || props.plans[0]?.code || '';
  const current = invoices.data?.[0];

  async function assign(): Promise<void> {
    if (!selected) return;
    setPending(true);
    try {
      await readJson(
        `/v1/tenants/${props.tenantId}/subscription`,
        { method: 'POST', body: JSON.stringify({ planCode: selected }) },
        null,
      );
      props.onChanged();
      void invoices.mutate();
      props.onNotice(
        'ok',
        text(
          'Ο πελάτης ενεργοποιήθηκε. Αν το πακέτο έχει τιμή, εκδόθηκε το τιμολόγιο του μήνα.',
          'The client was activated. If the plan has a price, this month’s invoice was issued.',
        ),
      );
    } catch (error) {
      props.onNotice('error', errorText(error, text('Η ανάθεση απέτυχε', 'Assignment failed')));
    } finally {
      setPending(false);
    }
  }

  async function setStatus(status: 'ACTIVE' | 'SUSPENDED'): Promise<void> {
    setPending(true);
    try {
      await readJson(
        `/v1/tenants/${props.tenantId}/subscription`,
        { method: 'PATCH', body: JSON.stringify({ status }) },
        null,
      );
      props.onChanged();
      props.onNotice('ok', text('Η συνδρομή ενημερώθηκε.', 'Subscription updated.'));
    } catch (error) {
      props.onNotice('error', errorText(error, text('Η ενημέρωση απέτυχε', 'Update failed')));
    } finally {
      setPending(false);
    }
  }

  async function issueInvoice(): Promise<void> {
    setPending(true);
    try {
      const result = await readJson<{ reason: string }>(
        `/v1/tenants/${props.tenantId}/invoices`,
        { method: 'POST' },
        null,
      );
      void invoices.mutate();
      props.onNotice(
        'ok',
        result.reason === 'no_price'
          ? text(
              'Το πακέτο δεν έχει τιμή, οπότε δεν εκδίδεται τιμολόγιο.',
              'The plan has no price, so no invoice is issued.',
            )
          : result.reason === 'exists'
            ? text(
                'Το τιμολόγιο αυτού του μήνα υπάρχει ήδη.',
                "This month's invoice already exists.",
              )
            : text('Το τιμολόγιο του μήνα εκδόθηκε.', "This month's invoice was issued."),
      );
    } catch (error) {
      props.onNotice('error', errorText(error, text('Η έκδοση απέτυχε', 'Issuing failed')));
    } finally {
      setPending(false);
    }
  }

  async function setInvoice(status: 'OPEN' | 'VOID'): Promise<void> {
    if (!current) return;
    setPending(true);
    try {
      await readJson(
        `/v1/tenants/${props.tenantId}/invoices/${current.id}`,
        { method: 'PATCH', body: JSON.stringify({ status }) },
        null,
      );
      void invoices.mutate();
      props.onNotice('ok', text('Το τιμολόγιο ενημερώθηκε.', 'Invoice updated.'));
    } catch (error) {
      props.onNotice(
        'error',
        errorText(error, text('Η ενημέρωση τιμολογίου απέτυχε', 'Invoice update failed')),
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <Card
      title={text('Συνδρομή και τιμολόγιο', 'Subscription and invoice')}
      description={text(
        'Η ανάθεση ενεργοποιεί τον πελάτη. Πακέτο με τιμή εκδίδει τιμολόγιο του τρέχοντος μήνα. Η πληρωμή σημειώνεται χειροκίνητα.',
        'Assigning a plan activates the client. A priced plan issues an invoice for the current month. Payments are recorded manually.',
      )}
      actions={props.subscription ? <Badge value={props.subscription.status} /> : null}
    >
      {props.loading ? (
        <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
          {text('Φόρτωση…', 'Loading…')}
        </p>
      ) : null}
      {props.subscription ? (
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
              <Icon name="layers" className="size-3.5" /> {text('Πακέτο', 'Plan')}
            </dt>
            <dd className="mt-0.5 font-medium text-slate-900 dark:text-slate-100">
              {props.subscription.plan.name}{' '}
              <span className="text-xs text-slate-400 dark:text-slate-500">
                {props.subscription.plan.code}
              </span>
            </dd>
          </div>
          <div>
            <dt className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
              <Icon name="pulse" className="size-3.5" /> {text('Έναρξη', 'Start')}
            </dt>
            <dd className="mt-0.5 font-medium text-slate-900 dark:text-slate-100">
              {formatDate(props.subscription.startsAt, locale)}
            </dd>
          </div>
          <div>
            <dt className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
              <Icon name="globe" className="size-3.5" /> {text('Ιστοσελίδες', 'Websites')}
            </dt>
            <dd className="mt-0.5 font-medium text-slate-900 dark:text-slate-100">
              {props.subscription.plan.limits.sites}
            </dd>
          </div>
          <div>
            <dt className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
              <Icon name="chat" className="size-3.5" />{' '}
              {text('Απαντήσεις / μήνα', 'Answers / month')}
            </dt>
            <dd className="mt-0.5 font-medium text-slate-900 dark:text-slate-100">
              {props.subscription.plan.limits.monthlyAnswers}
            </dd>
          </div>
          <div>
            <dt className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
              <Icon name="file" className="size-3.5" /> {text('Μηνιαία τιμή', 'Monthly price')}
            </dt>
            <dd className="mt-0.5 font-medium text-slate-900 dark:text-slate-100">
              {(props.subscription.plan.monthlyPriceCents ?? 0) > 0
                ? formatMoney(
                    props.subscription.plan.monthlyPriceCents ?? 0,
                    props.subscription.plan.currency ?? 'EUR',
                  )
                : text('Χωρίς χρέωση', 'Free')}
            </dd>
          </div>
        </dl>
      ) : !props.loading ? (
        <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
          {text('Ο πελάτης δεν έχει ενεργό πακέτο.', 'The client has no active plan.')}
        </p>
      ) : null}
      <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-slate-100 dark:border-slate-800 pt-4">
        <Field
          label={
            props.subscription
              ? text('Αλλαγή πακέτου', 'Change plan')
              : text('Ανάθεση πακέτου', 'Assign plan')
          }
        >
          <Select
            className="mt-1.5 min-w-48"
            value={selected}
            onChange={setPlanCode}
            options={props.plans.map((plan) => ({
              value: plan.code,
              label: `${plan.name} · ${
                plan.monthlyPriceCents > 0
                  ? formatMoney(plan.monthlyPriceCents, plan.currency)
                  : text('χωρίς τιμή', 'no price')
              }`,
            }))}
          />
        </Field>
        <Button type="button" onClick={() => void assign()} disabled={pending || !selected}>
          <Icon name="check" className="size-4" />{' '}
          {props.subscription
            ? text('Αλλαγή πακέτου', 'Change plan')
            : text('Ενεργοποίηση', 'Activate')}
        </Button>
        {props.subscription?.status === 'ACTIVE' ? (
          <Button
            type="button"
            variant="secondary"
            disabled={pending}
            onClick={() => void issueInvoice()}
          >
            <Icon name="file" className="size-4" /> {text('Τιμολόγιο μήνα', 'Monthly invoice')}
          </Button>
        ) : null}
        {props.subscription?.status === 'ACTIVE' ? (
          <Button
            type="button"
            variant="danger"
            disabled={pending}
            onClick={() => void setStatus('SUSPENDED')}
          >
            <Icon name="ban" className="size-4" />{' '}
            {text('Αναστολή συνδρομής', 'Suspend subscription')}
          </Button>
        ) : props.subscription?.status === 'SUSPENDED' ? (
          <Button
            type="button"
            variant="secondary"
            disabled={pending}
            onClick={() => void setStatus('ACTIVE')}
          >
            <Icon name="check" className="size-4" /> {text('Επανενεργοποίηση', 'Reactivate')}
          </Button>
        ) : null}
      </div>
      {current ? (
        <div className="mt-4 space-y-3 border-t border-slate-100 pt-4 dark:border-slate-800">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                {current.number} · {formatMoney(current.amountCents, current.currency)}
              </p>
              <p className="text-xs text-slate-500">
                {text('Λήξη', 'Due')} {formatDate(current.dueAt, locale)}
                {current.paidCents > 0
                  ? text(
                      ` · πληρωμένα ${formatMoney(current.paidCents, current.currency)} · υπόλοιπο ${formatMoney(current.balanceCents, current.currency)}`,
                      ` · paid ${formatMoney(current.paidCents, current.currency)} · balance ${formatMoney(current.balanceCents, current.currency)}`,
                    )
                  : ''}
              </p>
            </div>
            <Badge
              value={current.displayStatus}
              label={
                current.displayStatus === 'OPEN'
                  ? text('Απλήρωτο', 'Unpaid')
                  : current.displayStatus === 'PARTIAL'
                    ? text('Μερική', 'Partial')
                    : undefined
              }
            />
          </div>
          {current.status !== 'VOID' && current.status !== 'PAID' ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" disabled={pending} onClick={() => setPaying('full')}>
                <Icon name="banknote" className="size-4" /> {text('Πλήρης εξόφληση', 'Pay in full')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={pending}
                onClick={() => setPaying('partial')}
              >
                <Icon name="coins" className="size-4" /> {text('Μερική πληρωμή', 'Partial payment')}
              </Button>
              {current.paidCents > 0 ? (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={pending}
                  onClick={() => void setInvoice('OPEN')}
                >
                  <Icon name="undo" className="size-4" />{' '}
                  {text('Ακύρωση πληρωμών', 'Reverse payments')}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={pending}
                  onClick={() => void setInvoice('VOID')}
                >
                  <Icon name="x" className="size-4" /> {text('Ακύρωση τιμολογίου', 'Void invoice')}
                </Button>
              )}
            </div>
          ) : null}
          {current.status === 'PAID' ? (
            <Button
              type="button"
              variant="ghost"
              disabled={pending}
              onClick={() => void setInvoice('OPEN')}
            >
              <Icon name="undo" className="size-4" /> {text('Ακύρωση πληρωμών', 'Reverse payments')}
            </Button>
          ) : null}
        </div>
      ) : null}
      {paying && current ? (
        <InvoicePaymentDialog
          invoice={current}
          kind={paying}
          onClose={() => setPaying(null)}
          onSaved={() => {
            setPaying(null);
            void invoices.mutate();
            props.onNotice('ok', text('Η πληρωμή καταχωρίστηκε.', 'Payment recorded.'));
          }}
        />
      ) : null}
    </Card>
  );
}

function TenantSitesCard(props: {
  tenantId: string;
  sites: SiteRow[];
  loading: boolean;
  error: string | null;
  hasPlan: boolean;
  onChanged: () => void;
  onOpenAsTenant: (tenantId: string) => void;
}) {
  const { text } = useAdminLocale();
  const [name, setName] = useState('');
  const [origin, setOrigin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const paging = useClientPaging(props.sites, { resetKey: props.tenantId });

  async function create(event: FormEvent): Promise<void> {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await readJson(
        `/v1/tenants/${props.tenantId}/sites`,
        { method: 'POST', body: JSON.stringify({ name, canonicalOrigin: origin }) },
        null,
      );
      setName('');
      setOrigin('');
      props.onChanged();
    } catch (caught) {
      setError(errorText(caught, text('Η δημιουργία απέτυχε', 'Creation failed')));
    } finally {
      setPending(false);
    }
  }

  return (
    <Card
      title={text('Ιστοσελίδες', 'Websites')}
      description={text(
        'Το origin πρέπει να είναι ακριβές (www και γυμνό domain είναι διαφορετικά).',
        'The origin must be exact (www and the bare domain are different).',
      )}
      flush
    >
      <div className="border-b border-slate-100 dark:border-slate-800 px-5 py-4">
        <form
          onSubmit={(event) => void create(event)}
          className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end"
        >
          <Field label={text('Όνομα', 'Name')}>
            <TextInput required value={name} onChange={(event) => setName(event.target.value)} />
          </Field>
          <Field label="Production origin">
            <TextInput
              required
              type="url"
              value={origin}
              onChange={(event) => setOrigin(event.target.value)}

              placeholder="https://example.gr"
            />
          </Field>
          <Button type="submit" disabled={pending || !props.hasPlan}>
            <Icon name="plus" className="size-4" />
            {text('Νέα ιστοσελίδα', 'New website')}
          </Button>
        </form>
        {!props.hasPlan ? (
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
            {text('Αναθέστε πρώτα πακέτο στον πελάτη.', 'Assign a plan to the client first.')}
          </p>
        ) : null}
        {error ? <p className="mt-2 text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}
      </div>
      {props.loading ? (
        <p className="px-5 py-4 text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
          {text('Φόρτωση…', 'Loading…')}
        </p>
      ) : props.error ? (
        <p className="px-5 py-4 text-sm text-rose-700 dark:text-rose-300">{props.error}</p>
      ) : props.sites.length === 0 ? (
        <p className="px-5 py-4 text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
          {text('Δεν υπάρχουν ιστοσελίδες.', 'No websites found.')}
        </p>
      ) : (
        <PagedTableShell pagination={paging.pager}>
          <Table head={[text('Ιστοσελίδα', 'Website'), 'Origin', text('Κατάσταση', 'Status'), '']}>
            {paging.rows.map((site) => (
              <tr key={site.id}>
                <Td>
                  <div className="flex items-center gap-3">
                    <span className="grid size-9 shrink-0 place-items-center rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-600">
                      <Icon name="globe" className="size-4" />
                    </span>
                    <span className="font-medium text-slate-900 dark:text-slate-100">
                      {site.name}
                    </span>
                  </div>
                </Td>
                <Td muted>{site.canonicalOrigin}</Td>
                <Td>
                  <Badge value={site.status} />
                </Td>
                <Td className="text-right">
                  <button
                    type="button"
                    className={`${linkClass} inline-flex items-center gap-1`}
                    onClick={() => props.onOpenAsTenant(props.tenantId)}
                  >
                    {text('Διαχείριση', 'Manage')} <Icon name="arrow" className="size-4" />
                  </button>
                </Td>
              </tr>
            ))}
          </Table>
        </PagedTableShell>
      )}
    </Card>
  );
}

const EMPTY_LIMITS: PlanLimits = {
  sites: 1,
  monthlyAnswers: 1000,
  spendingCeilingUsd: null,
  maxSources: 500,
  maxPdfBytes: 10 * 1024 * 1024,
  maxExtractedChars: 2_000_000,
  maxInputTokens: 8_000,
  maxOutputTokens: 400,
};

function number(value: string): number {
  return Number.parseInt(value, 10) || 0;
}

export function PlansPage() {
  const { locale, text } = useAdminLocale();
  const remote = useRemote<PlanRow[]>('/v1/plans', null);
  const paging = useClientPaging(remote.data ?? []);
  const rows = paging.rows;
  const [editing, setEditing] = useState<PlanRow | 'new' | null>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  return (
    <section className="admin-page">
      <PageHeader
        title={text('Πακέτα', 'Plans')}
        badge={
          <span className="admin-mono rounded-[4px] border border-[color:var(--admin-border)] px-2 py-1 text-[10px] tracking-[0.08em] text-slate-500 uppercase dark:border-slate-700 dark:text-slate-400">
            {paging.total} {text('ενεργά', 'active')}
          </span>
        }
        description={text(
          'Όρια και μηνιαία τιμή ανά πακέτο. Τιμή 0 δεν εκδίδει τιμολόγιο. Η πληρωμή σημειώνεται στη Τιμολόγηση — δεν υπάρχει κάρτα.',
          'Limits and monthly price for each plan. A price of 0 does not issue an invoice. Payments are recorded in Billing—there is no card processing.',
        )}
        actions={
          <Button type="button" onClick={() => setEditing('new')}>
            <Icon name="plus" className="size-4" /> {text('Νέο πακέτο', 'New plan')}
          </Button>
        }
      />
      {notice ? (
        <p
          className={`text-sm ${notice.tone === 'ok' ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}
        >
          {notice.text}
        </p>
      ) : null}
      {editing ? (
        <PlanForm
          plan={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setNotice({
              tone: 'ok',
              text: text('Το πακέτο αποθηκεύτηκε.', 'Plan saved.'),
            });
            void remote.mutate();
          }}
        />
      ) : null}
      <Panel
        loading={!remote.data && !remote.error}
        error={remote.error?.message ?? null}
        empty={paging.total === 0}
        emptyLabel={text('Δεν υπάρχουν πακέτα.', 'No plans found.')}
      >
        <div className="flex min-h-0 flex-col overflow-hidden rounded-[var(--admin-radius)] border border-[color:var(--admin-border)] bg-[color:var(--admin-surface)]">
          <div className="flex items-center justify-between border-b border-[color:var(--admin-border)] bg-[color:var(--admin-paper)] px-5 py-3 dark:bg-slate-950/50">
            <span className="admin-mono text-[10px] tracking-[0.14em] text-slate-500 uppercase">
              Plan registry
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {text('Επιλέξτε πακέτο για επεξεργασία', 'Select a plan to edit')}
            </span>
          </div>
          <div className="grid gap-px bg-[color:var(--admin-border)] md:grid-cols-2 xl:grid-cols-3">
            {rows.map((plan) => (
              <button
                key={plan.id}
                type="button"
                onClick={() => setEditing(plan)}
                className="group relative flex min-h-0 flex-col bg-[color:var(--admin-surface)] p-4 text-left transition hover:bg-[color:var(--admin-paper)] focus:z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--admin-accent)] dark:hover:bg-slate-950"
              >
                <span className="absolute top-0 bottom-0 left-0 w-1 bg-[color:var(--admin-accent)] opacity-40 transition group-hover:opacity-100" />
                <span className="flex items-start justify-between gap-4">
                  <span className="grid size-9 place-items-center rounded-[var(--admin-radius-sm)] border border-[color:var(--admin-border)] text-[color:var(--admin-accent)]">
                    <Icon name="layers" className="size-4" />
                  </span>
                  <span className="admin-mono rounded-[4px] bg-[color:var(--admin-paper)] px-2 py-1 text-[10px] tracking-[0.08em] text-slate-500 uppercase dark:bg-slate-800 dark:text-slate-400">
                    {plan.code}
                  </span>
                </span>
                <span className="mt-4 flex items-end justify-between gap-3">
                  <span>
                    <span className="admin-display block text-xl font-semibold tracking-[-0.04em] text-slate-950 dark:text-slate-100">
                      {plan.name}
                    </span>
                    <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
                      {plan.monthlyPriceCents > 0
                        ? text(
                            `${formatMoney(plan.monthlyPriceCents, plan.currency)} / μήνα`,
                            `${formatMoney(plan.monthlyPriceCents, plan.currency)} / month`,
                          )
                        : text('Χωρίς μηνιαία χρέωση', 'No monthly charge')}
                    </span>
                  </span>
                  <Icon
                    name="arrow"
                    className="size-4 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-[color:var(--admin-accent)]"
                  />
                </span>
                <span className="mt-4 grid grid-cols-2 border-y border-[color:var(--admin-border)] py-2.5">
                  <span className="border-r border-[color:var(--admin-border)] pr-3">
                    <span className="admin-mono block text-xl font-semibold text-slate-950 dark:text-slate-100">
                      {plan.limits.sites.toLocaleString(locale === 'en' ? 'en-GB' : 'el-GR')}
                    </span>
                    <span className="admin-mono block text-[9px] tracking-[0.1em] text-slate-500 uppercase">
                      websites
                    </span>
                  </span>
                  <span className="pl-3">
                    <span className="admin-mono block text-xl font-semibold text-slate-950 dark:text-slate-100">
                      {plan.limits.monthlyAnswers.toLocaleString(
                        locale === 'en' ? 'en-GB' : 'el-GR',
                      )}
                    </span>
                    <span className="admin-mono block text-[9px] tracking-[0.1em] text-slate-500 uppercase">
                      {text('απαντήσεις / μήνα', 'answers / month')}
                    </span>
                  </span>
                </span>
                {plan.limits.spendingCeilingUsd != null || plan.limits.maxConcurrent != null ? (
                  <span className="mt-3 block text-[11px] text-slate-500 dark:text-slate-400">
                    {plan.limits.spendingCeilingUsd != null
                      ? text(
                          `Όριο δαπάνης ${plan.limits.spendingCeilingUsd.toLocaleString('el-GR')} USD`,
                          `Spending limit ${plan.limits.spendingCeilingUsd.toLocaleString('en-GB')} USD`,
                        )
                      : null}
                    {plan.limits.spendingCeilingUsd != null && plan.limits.maxConcurrent != null
                      ? ' · '
                      : null}
                    {plan.limits.maxConcurrent != null
                      ? text(
                          `${plan.limits.maxConcurrent.toLocaleString('el-GR')} ταυτόχρονες`,
                          `${plan.limits.maxConcurrent.toLocaleString('en-GB')} concurrent`,
                        )
                      : null}
                  </span>
                ) : null}
                <span className="admin-mono mt-3 pt-1 text-[10px] tracking-[0.1em] text-slate-400 uppercase transition group-hover:text-[color:var(--admin-accent)]">
                  Edit plan
                </span>
              </button>
            ))}
          </div>
          {paging.total > 0 ? <ListPagination {...paging.pager} /> : null}
        </div>
      </Panel>
    </section>
  );
}

function PlanForm(props: { plan: PlanRow | null; onClose: () => void; onSaved: () => void }) {
  const { text } = useAdminLocale();
  const [code, setCode] = useState(props.plan?.code ?? '');
  const [name, setName] = useState(props.plan?.name ?? '');
  const [limits, setLimits] = useState<PlanLimits>(props.plan?.limits ?? EMPTY_LIMITS);
  const [price, setPrice] = useState(
    props.plan ? (props.plan.monthlyPriceCents / 100).toFixed(2) : '0',
  );
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [step, setStep] = useState<'basics' | 'limits'>('basics');

  function basicsReady(): boolean {
    if (!props.plan && !/^[a-z0-9-]+$/.test(code.trim())) {
      setError(
        text(
          'Ο κωδικός χρειάζεται πεζά λατινικά, αριθμούς και παύλες.',
          'The code must contain lowercase Latin letters, numbers, and hyphens.',
        ),
      );
      return false;
    }
    if (!name.trim()) {
      setError(text('Συμπλήρωσε το όνομα του πακέτου.', 'Enter the plan name.'));
      return false;
    }
    const euros = Number.parseFloat(price.replace(',', '.'));
    if (!Number.isFinite(euros) || euros < 0) {
      setError(text('Η μηνιαία τιμή δεν είναι έγκυρη.', 'The monthly price is invalid.'));
      return false;
    }
    setError(null);
    return true;
  }

  function openStep(next: 'basics' | 'limits'): void {
    if (next === 'limits' && !basicsReady()) return;
    setError(null);
    setStep(next);
  }

  async function save(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!basicsReady()) return;
    setPending(true);
    setError(null);
    const euros = Number.parseFloat(price.replace(',', '.'));
    const body: Record<string, unknown> = {
      name,
      monthlyPriceCents: Math.round(euros * 100),
      limits: {
        sites: limits.sites,
        monthlyAnswers: limits.monthlyAnswers,
        spendingCeilingUsd: limits.spendingCeilingUsd ?? null,
        ...(limits.siteMonthlyAnswers !== undefined
          ? { siteMonthlyAnswers: limits.siteMonthlyAnswers }
          : {}),
        ...(limits.maxConcurrent !== undefined ? { maxConcurrent: limits.maxConcurrent } : {}),
        ...(limits.ingestionMonthlyTokens !== undefined
          ? { ingestionMonthlyTokens: limits.ingestionMonthlyTokens }
          : {}),
        ...(limits.maxSources !== undefined ? { maxSources: limits.maxSources } : {}),
        ...(limits.maxPdfBytes !== undefined ? { maxPdfBytes: limits.maxPdfBytes } : {}),
        ...(limits.maxExtractedChars !== undefined
          ? { maxExtractedChars: limits.maxExtractedChars }
          : {}),
        ...(limits.maxInputTokens !== undefined ? { maxInputTokens: limits.maxInputTokens } : {}),
        ...(limits.maxOutputTokens !== undefined
          ? { maxOutputTokens: limits.maxOutputTokens }
          : {}),
      },
    };
    try {
      if (props.plan) {
        await readJson(
          `/v1/plans/${props.plan.id}`,
          { method: 'PATCH', body: JSON.stringify(body) },
          null,
        );
      } else {
        await readJson(
          '/v1/plans',
          { method: 'POST', body: JSON.stringify({ code, ...body }) },
          null,
        );
      }
      props.onSaved();
    } catch (caught) {
      setError(errorText(caught, text('Η αποθήκευση απέτυχε', 'Save failed')));
    } finally {
      setPending(false);
    }
  }

  return (
    <Modal
      wide
      title={props.plan ? props.plan.name : text('Νέο πακέτο', 'New plan')}
      description={
        step === 'basics'
          ? text(
              'Αυτά ορίζουν το πακέτο. Η αποθήκευση γίνεται από εδώ. Τα τεχνικά όρια είναι προαιρετικά.',
              'These fields define the plan. Save it here. Technical limits are optional.',
            )
          : text(
              'Άφησέ τα κενά για τα προεπιλεγμένα όρια της πλατφόρμας. Συμπλήρωσε μόνο ό,τι πρέπει να διαφέρει σε αυτό το πακέτο.',
              'Leave fields blank to use platform defaults. Only enter values that differ for this plan.',
            )
      }
      onClose={props.onClose}
    >
      <form onSubmit={(event) => void save(event)} className="space-y-4">
        <Tabs
          items={[
            { id: 'basics', label: text('1. Βασικά', '1. Basics'), icon: 'layers' },
            {
              id: 'limits',
              label: text('2. Προαιρετικά όρια', '2. Optional limits'),
              icon: 'settings',
            },
          ]}
          value={step}
          onChange={(id) => openStep(id === 'limits' ? 'limits' : 'basics')}
        />
        {step === 'basics' ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={text('Κωδικός', 'Code')}
              hint={text(
                'Πεζά λατινικά, αριθμοί και παύλες. Δεν αλλάζει μετά.',
                'Lowercase Latin letters, numbers, and hyphens. It cannot be changed later.',
              )}
            >
              <TextInput
                required
                pattern="[a-z0-9-]+"
                disabled={Boolean(props.plan)}
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            </Field>
            <Field label={text('Όνομα', 'Name')}>
              <TextInput required value={name} onChange={(event) => setName(event.target.value)} />
            </Field>
            <Field
              label={text('Μηνιαία τιμή (EUR)', 'Monthly price (EUR)')}
              hint={text(
                '0 = δοκιμή ή εσωτερική χρήση, χωρίς τιμολόγιο.',
                '0 = trial or internal use, without an invoice.',
              )}
            >
              <TextInput
                required
                inputMode="decimal"
                value={price}
                onChange={(event) => setPrice(event.target.value)}
              />
            </Field>
            <Field label={text('Ιστοσελίδες', 'Websites')}>
              <TextInput
                type="number"
                min={0}
                required
                value={limits.sites}
                onChange={(event) => setLimits({ ...limits, sites: number(event.target.value) })}
              />
            </Field>
            <Field
              label={text('Απαντήσεις / μήνα', 'Answers / month')}
              hint={text(
                'Πόσες απαντήσεις μπορεί να δώσει το chatbot μέσα στον μήνα.',
                'How many answers the chatbot can provide during the month.',
              )}
            >
              <TextInput
                type="number"
                min={0}
                required
                value={limits.monthlyAnswers}
                onChange={(event) =>
                  setLimits({ ...limits, monthlyAnswers: number(event.target.value) })
                }
              />
            </Field>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={text('Όριο δαπάνης AI (USD)', 'AI spending limit (USD)')}
              hint={text(
                'Εκτιμώμενο κόστος OpenAI του μήνα. Όταν ξεπεραστεί, το chatbot δεν ξεκινά νέες απαντήσεις ως τον επόμενο μήνα. Κενό = χωρίς ταβάνι.',
                'Estimated monthly OpenAI cost. When exceeded, the chatbot will not start new answers until the next month. Blank = no ceiling.',
              )}
            >
              <TextInput
                type="number"
                min={0}
                step="0.01"
                value={limits.spendingCeilingUsd ?? ''}
                onChange={(event) =>
                  setLimits({
                    ...limits,
                    spendingCeilingUsd:
                      event.target.value === '' ? null : Number.parseFloat(event.target.value),
                  })
                }
              />
            </Field>
            <Field
              label={text('Μέγιστες ταυτόχρονες κλήσεις', 'Maximum concurrent calls')}
              hint={text(
                'Πόσες απαντήσεις μπορούν να ετοιμάζονται ταυτόχρονα για τον πελάτη. Νέα απάντηση δεν ξεκινά όσο είναι όλες πιασμένες. Κενό = 8.',
                'How many answers can be prepared concurrently for the client. A new answer will not start while all slots are occupied. Blank = 8.',
              )}
            >
              <TextInput
                type="number"
                min={1}
                value={limits.maxConcurrent ?? ''}
                onChange={(event) => {
                  const next = { ...limits };
                  if (event.target.value === '') delete next.maxConcurrent;
                  else next.maxConcurrent = number(event.target.value);
                  setLimits(next);
                }}
              />
            </Field>
            <Field
              label={text('Μέγιστες πηγές γνώσης', 'Maximum knowledge sources')}
              hint={text(
                'Πόσες πηγές (σελίδες, PDF, κείμενα) χωράει κάθε ιστοσελίδα. Νέα πηγή πάνω από το όριο απορρίπτεται. Κενό = 500.',
                'How many sources (pages, PDFs, text) each website can hold. New sources over the limit are rejected. Blank = 500.',
              )}
            >
              <TextInput
                type="number"
                min={0}
                value={limits.maxSources ?? ''}
                onChange={(event) => {
                  const next = { ...limits };
                  if (event.target.value === '') delete next.maxSources;
                  else next.maxSources = number(event.target.value);
                  setLimits(next);
                }}
              />
            </Field>
            <Field
              label={text('Μέγιστο μέγεθος PDF (bytes)', 'Maximum PDF size (bytes)')}
              hint={text(
                'Μεγαλύτερο PDF δεν ανεβαίνει. Το νούμερο είναι σε bytes: 10 MB = 10.485.760. Ισχύει το μικρότερο από αυτό και το όριο της πλατφόρμας. Κενό = 10 MB.',
                'Larger PDFs cannot be uploaded. The value is in bytes: 10 MB = 10,485,760. The lower of this value and the platform limit applies. Blank = 10 MB.',
              )}
            >
              <TextInput
                type="number"
                min={1}
                value={limits.maxPdfBytes ?? ''}
                onChange={(event) => {
                  const next = { ...limits };
                  if (event.target.value === '') delete next.maxPdfBytes;
                  else next.maxPdfBytes = number(event.target.value);
                  setLimits(next);
                }}
              />
            </Field>
            <Field
              label={text('Μέγιστοι χαρακτήρες εξαγωγής', 'Maximum extracted characters')}
              hint={text(
                'Πόσο κείμενο επιτρέπεται να διαβαστεί από ένα PDF ή μία σελίδα. Μεγαλύτερο κείμενο δεν γίνεται γνώση. Κενό = 2.000.000 χαρακτήρες.',
                'How much text can be read from a PDF or page. Larger text cannot become knowledge. Blank = 2,000,000 characters.',
              )}
            >
              <TextInput
                type="number"
                min={1}
                value={limits.maxExtractedChars ?? ''}
                onChange={(event) => {
                  const next = { ...limits };
                  if (event.target.value === '') delete next.maxExtractedChars;
                  else next.maxExtractedChars = number(event.target.value);
                  setLimits(next);
                }}
              />
            </Field>
            <Field
              label={text('Μέγιστα tokens εισόδου', 'Maximum input tokens')}
              hint={text(
                'Πόσο κείμενο χωράει σε μία κλήση προς το μοντέλο: η ερώτηση μαζί με τα αποσπάσματα γνώσης. Προεπιλογή 8.000.',
                'How much text fits in one model call: the question plus knowledge excerpts. Default 8,000.',
              )}
            >
              <TextInput
                type="number"
                min={1}
                value={limits.maxInputTokens ?? ''}
                onChange={(event) => {
                  const next = { ...limits };
                  if (event.target.value === '') delete next.maxInputTokens;
                  else next.maxInputTokens = number(event.target.value);
                  setLimits(next);
                }}
              />
            </Field>
            <Field
              label={text('Μέγιστα tokens απάντησης', 'Maximum answer tokens')}
              hint={text(
                'Μέγιστο μήκος της απάντησης του chatbot. Μικρότερο νούμερο δίνει πιο σύντομες και φθηνότερες απαντήσεις. Κενό = 400.',
                'Maximum chatbot answer length. A smaller value produces shorter, less expensive answers. Blank = 400.',
              )}
            >
              <TextInput
                type="number"
                min={1}
                value={limits.maxOutputTokens ?? ''}
                onChange={(event) => {
                  const next = { ...limits };
                  if (event.target.value === '') delete next.maxOutputTokens;
                  else next.maxOutputTokens = number(event.target.value);
                  setLimits(next);
                }}
              />
            </Field>
          </div>
        )}
        {error ? <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}
        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-100 pt-4 dark:border-slate-800">
          {step === 'limits' ? (
            <Button type="button" variant="secondary" onClick={() => openStep('basics')}>
              <Icon name="arrow" className="size-4 rotate-180" /> {text('Βασικά', 'Basics')}
            </Button>
          ) : (
            <Button type="button" variant="secondary" onClick={props.onClose}>
              <Icon name="x" className="size-4" /> {text('Άκυρο', 'Cancel')}
            </Button>
          )}
          {step === 'basics' ? (
            <Button type="button" variant="secondary" onClick={() => openStep('limits')}>
              <Icon name="settings" className="size-4" />{' '}
              {text('Προαιρετικά όρια', 'Optional limits')}
            </Button>
          ) : null}
          <Button type="submit" disabled={pending}>
            <Icon name="save" className="size-4" />
            {pending ? text('Αποθήκευση…', 'Saving…') : text('Αποθήκευση πακέτου', 'Save plan')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
